import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ExternalLink, Copy, Pin, MoreVertical, Pencil, Trash2, Check } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { ICONS, ICON_TINT, BADGE_COLORS, STATUS_META } from "../../lib/toolMeta";

const shortUrl = (url) => {
  try {
    const u = new URL(url);
    return (u.hostname + u.pathname).replace(/\/$/, "").replace(/^www\./, "");
  } catch {
    return url;
  }
};

export const ToolCard = ({ tool, index, onEdit, onDelete, onTogglePin }) => {
  const [copied, setCopied] = useState(false);
  const Icon = ICONS[tool.icon] || ICONS.link;
  const status = STATUS_META[tool.status] || STATUS_META.online;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(tool.url);
      setCopied(true);
      toast.success("Link copiato negli appunti");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.4), ease: [0.16, 1, 0.3, 1] }}
      data-testid={`app-card-${tool.id}`}
      className="group relative flex flex-col rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${ICON_TINT[tool.badgeColor] || ICON_TINT.slate}`}>
          <Icon className="h-6 w-6" strokeWidth={2} />
        </div>
        <div className="flex items-center gap-1.5">
          {tool.isPinned && (
            <Pin className="h-4 w-4 fill-primary text-primary" data-testid={`app-pinned-${tool.id}`} />
          )}
          <div className="flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1">
            <span className={`h-2 w-2 rounded-full ${status.dot} led-pulse`} />
            <span className="text-[10px] font-medium text-muted-foreground">{status.label}</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`app-menu-button-${tool.id}`}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem data-testid={`app-pin-button-${tool.id}`} onClick={() => onTogglePin(tool)}>
                <Pin className="mr-2 h-4 w-4" />
                {tool.isPinned ? "Rimuovi dai fissati" : "Fissa in alto"}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`app-edit-button-${tool.id}`} onClick={() => onEdit(tool)}>
                <Pencil className="mr-2 h-4 w-4" /> Modifica
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid={`app-delete-button-${tool.id}`}
                onClick={() => onDelete(tool)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Elimina
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="mt-4 flex-1">
        <h3 className="font-display text-lg font-semibold tracking-tight text-foreground">{tool.name}</h3>
        <span className={`mt-2 inline-block rounded-md px-2 py-0.5 text-xs font-medium ${BADGE_COLORS[tool.badgeColor] || BADGE_COLORS.slate}`}>
          {tool.category}
        </span>
        {tool.note && (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-3">{tool.note}</p>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between gap-2 border-t border-border pt-4">
        <span className="truncate font-mono text-xs text-muted-foreground">{shortUrl(tool.url)}</span>
        <div className="flex items-center gap-1.5">
          <button
            data-testid={`app-copy-button-${tool.id}`}
            onClick={copyLink}
            className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            title="Copia link"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          </button>
          <a
            data-testid={`app-launch-link-${tool.id}`}
            href={tool.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Apri <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </motion.div>
  );
};
